/**
 * Phase K Pass 2 — Today tab v2.
 *
 * URGENT (auto-hides empty) · QUEUE · SNAPSHOT bands + activity feed.
 * Driven by a single composite endpoint: GET /admin/dashboard/today-summary.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    AlertTriangle, XCircle, ShieldAlert, FileText, FileSignature,
    Calendar, TrendingUp, Bell, ArrowUpRight, CheckCircle2, Receipt,
} from "lucide-react";
// Phase TOLLBILL-P5d UX tweak (2026-07-10) — the Unbilled toll bills
// widget now renders below the Snapshot band (was above the tabs).
import TollsUnbilledDashboardWidget from "@/components/TollsUnbilledDashboardWidget";

function fmtMoney(cents) {
    if (cents == null) return "$0.00";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtRel(iso) {
    if (!iso) return "—";
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const min = Math.floor(diff / 60000);
        if (min < 1) return "just now";
        if (min < 60) return `${min}m ago`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr}h ago`;
        const d = Math.floor(hr / 24);
        if (d < 30) return `${d}d ago`;
        const mon = Math.floor(d / 30);
        return `${mon}mo ago`;
    } catch {
        return iso;
    }
}

function UrgentTile({ testId, icon: Icon, label, value, sub, href }) {
    const inner = (
        <div
            data-testid={testId}
            className="rounded-lg ring-1 ring-red-200 bg-red-50/60 p-3 h-full transition-colors hover:ring-2"
        >
            <div className="flex items-center justify-between mb-1.5 text-red-700">
                <span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span>
                <Icon size={14} />
            </div>
            <div className="font-display text-2xl font-bold tracking-tight tabular-nums text-red-700">
                {value}
            </div>
            {sub && <div className="text-[11px] mt-0.5 text-red-700/70">{sub}</div>}
        </div>
    );
    return href ? <Link to={href}>{inner}</Link> : inner;
}

function QueueTile({ testId, icon: Icon, label, value, sub, href, tone = "blue" }) {
    const accents = {
        blue: "bg-blue-50 text-blue-900 ring-blue-200",
        amber: "bg-amber-50 text-amber-900 ring-amber-200",
        slate: "bg-slate-100 text-slate-800 ring-slate-200",
    };
    const inner = (
        <div data-testid={testId} className={`rounded-lg ring-1 p-3 h-full transition-colors hover:ring-2 ${accents[tone] || accents.blue}`}>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span>
                <Icon size={14} />
            </div>
            <div className="font-display text-2xl font-bold tracking-tight tabular-nums">{value}</div>
            {sub && <div className="text-[11px] mt-0.5 opacity-75">{sub}</div>}
        </div>
    );
    return href ? <Link to={href}>{inner}</Link> : inner;
}

const ACTIVITY_ICON = {
    auto_charge_disabled_by_pm_removal: AlertTriangle,
    auto_charge_failed: XCircle,
    auto_charge_initiated: TrendingUp,
    auto_charge_attempted: TrendingUp,
    auto_charge_succeeded: CheckCircle2,
    auto_charge_abandoned: AlertTriangle,
    invoice_paid: Receipt,
    invoice_overdue: AlertTriangle,
    coi_expiring: ShieldAlert,
    coi_expired: ShieldAlert,
    contract_signed: FileSignature,
    safer_risk_flag_raised: ShieldAlert,
};

function ActivityRow({ row }) {
    const Icon = ACTIVITY_ICON[row.event_type] || Bell;
    return (
        <li
            data-testid={`today-activity-row-${row.id}`}
            className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0"
        >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-600 shrink-0 mt-0.5">
                <Icon size={13} />
            </span>
            <div className="min-w-0 flex-1">
                {row.cta_url ? (
                    <Link to={row.cta_url} className="text-sm font-medium text-slate-900 hover:text-blue-900 truncate block">
                        {row.title || row.event_type}
                    </Link>
                ) : (
                    <p className="text-sm font-medium text-slate-900 truncate">{row.title || row.event_type}</p>
                )}
                <p className="text-[11px] text-slate-500 mt-0.5">{fmtRel(row.created_at)}</p>
            </div>
        </li>
    );
}

export function TodayTabPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/dashboard/today-summary");
                setData(data);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return <div data-testid="today-tab-panel" className="mt-2 text-sm text-slate-500">Loading today's summary…</div>;
    }

    const urgent = data?.urgent || {};
    const queue = data?.queue || {};
    const snapshot = data?.snapshot || {};
    const feed = data?.since_last_login?.items || [];
    const anchor = data?.since_last_login?.anchor_at;

    const urgentItems = [
        {
            key: "overdue", label: "Overdue invoices", icon: Receipt,
            value: urgent.overdue_invoices?.count ?? 0,
            sub: urgent.overdue_invoices?.amount_cents
                ? fmtMoney(urgent.overdue_invoices.amount_cents)
                : null,
            href: "/admin/invoices?status=overdue",
        },
        {
            key: "failed", label: "Failed payments", icon: XCircle,
            value: urgent.failed_payments_month?.count ?? 0,
            sub: snapshot.month ? `month ${snapshot.month}` : null,
            href: "/admin/payments?status=failed",
        },
        {
            key: "abandoned", label: "Abandoned auto-charges", icon: AlertTriangle,
            value: urgent.abandoned_auto_charges_month?.count ?? 0,
            sub: snapshot.month ? `month ${snapshot.month}` : null,
            href: "/admin/auto-charge-attempts?status=abandoned",
        },
        {
            key: "coi-7d", label: "COIs expiring ≤7d", icon: ShieldAlert,
            value: urgent.coi_expiring_le_7d?.count ?? 0,
            sub: "next 7 days",
            href: "/admin/rentals?coi_expiring=1",
        },
    ];
    const urgentVisible = urgentItems.filter((it) => (it.value || 0) > 0);

    const queueItems = [
        {
            key: "pending-review", label: "Pending review", icon: FileText,
            value: queue.pending_review_submissions?.count ?? 0,
            sub: "Submissions awaiting your review",
            href: "/admin/submissions?status=submitted",
            tone: "blue",
        },
        {
            key: "pending-sigs", label: "Pending signatures", icon: FileSignature,
            value: queue.pending_signatures?.count ?? 0,
            sub: queue.pending_signatures?.oldest_days != null
                ? `oldest ${queue.pending_signatures.oldest_days}d`
                : "contracts awaiting customer",
            href: "/admin/contracts?status=sent_for_signature",
            tone: "amber",
        },
        {
            key: "renewals", label: "Renewals next 30d", icon: Calendar,
            value: queue.renewal_radar_30d?.count ?? 0,
            sub: "rentals reaching min-term",
            href: "/admin/rentals/renewals",
            tone: "blue",
        },
        {
            key: "safer", label: "SAFER risk flags", icon: ShieldAlert,
            value: queue.safer_risk_flags?.count ?? 0,
            sub: "active customers flagged",
            href: "/admin/customers?safer_risk=1",
            tone: "slate",
        },
    ];

    return (
        <div data-testid="today-tab-panel" className="space-y-6 mt-2">
            {/* URGENT band — only renders if at least one tile has count > 0 */}
            {urgentVisible.length > 0 && (
                <section data-testid="today-band-urgent">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-red-700">
                            <AlertTriangle size={14} />
                        </span>
                        <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-red-800">Urgent</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {urgentVisible.map((it) => (
                            <UrgentTile
                                key={it.key}
                                testId={`today-urgent-${it.key}`}
                                icon={it.icon}
                                label={it.label}
                                value={it.value}
                                sub={it.sub}
                                href={it.href}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* QUEUE band */}
            <section data-testid="today-band-queue">
                <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-900">
                        <FileText size={14} />
                    </span>
                    <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-blue-900">Queue</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {queueItems.map((it) => (
                        <QueueTile
                            key={it.key}
                            testId={`today-queue-${it.key}`}
                            icon={it.icon}
                            label={it.label}
                            value={it.value}
                            sub={it.sub}
                            href={it.href}
                            tone={it.tone}
                        />
                    ))}
                </div>
            </section>

            {/* SNAPSHOT band */}
            <section data-testid="today-band-snapshot">
                <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-900">
                        <TrendingUp size={14} />
                    </span>
                    <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-emerald-900">
                        Snapshot {snapshot.month && <span className="ml-1 text-emerald-700">· {snapshot.month}</span>}
                    </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Link
                        to="/admin/payments?status=succeeded"
                        data-testid="today-snapshot-collected"
                        className="rounded-lg ring-1 ring-emerald-200 bg-emerald-50/60 p-4 transition-colors hover:ring-2"
                    >
                        <div className="flex items-center justify-between mb-1.5 text-emerald-900">
                            <span className="text-[11px] font-semibold uppercase tracking-wider">Collected</span>
                            <TrendingUp size={14} />
                        </div>
                        <div className="font-display text-3xl font-bold tracking-tight tabular-nums text-emerald-900">
                            {fmtMoney(snapshot.collected_cents)}
                        </div>
                    </Link>
                    <Link
                        to="/admin/invoices?status=overdue"
                        data-testid="today-snapshot-outstanding"
                        className="rounded-lg ring-1 ring-slate-200 bg-white p-4 transition-colors hover:ring-2"
                    >
                        <div className="flex items-center justify-between mb-1.5 text-slate-700">
                            <span className="text-[11px] font-semibold uppercase tracking-wider">Outstanding</span>
                            <Receipt size={14} />
                        </div>
                        <div className="font-display text-3xl font-bold tracking-tight tabular-nums text-slate-900">
                            {fmtMoney(snapshot.outstanding_cents)}
                        </div>
                    </Link>
                </div>
            </section>

            {/* Phase TOLLBILL-P5d UX tweak — Unbilled toll bills widget
                renders directly below the Snapshot band. Internally
                gated by usePermission("Tolls", "View") — self-hides for
                roles that lack the permission. */}
            <TollsUnbilledDashboardWidget />

            {/* Activity feed */}
            <section data-testid="today-band-activity">
                <Card>
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                                <Bell size={14} className="text-slate-500" />
                                Last activity since you logged out
                                {anchor && (
                                    <span className="ml-2 text-xs font-medium text-slate-500" data-testid="today-activity-anchor">
                                        ({fmtRel(anchor)})
                                    </span>
                                )}
                            </h2>
                            <Link
                                to="/admin/notifications"
                                data-testid="today-activity-view-all"
                                className="text-xs font-medium text-blue-900 hover:underline inline-flex items-center gap-1"
                            >
                                View all <ArrowUpRight size={11} />
                            </Link>
                        </div>
                        {feed.length === 0 ? (
                            <p className="text-sm text-slate-500" data-testid="today-activity-empty">
                                Nothing has happened since you last logged in.
                            </p>
                        ) : (
                            <ul>
                                {feed.map((row) => <ActivityRow key={row.id} row={row} />)}
                            </ul>
                        )}
                    </CardContent>
                </Card>
            </section>
        </div>
    );
}

export default TodayTabPanel;
